import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import PlanCard from './components/PlanCard';
import PaymentModal from './components/PaymentModal';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const BASIC_FEATURES = [
  { text: '浏览会员基本资料', included: true },
  { text: '每日10次喜欢', included: true },
  { text: '查看谁喜欢了您', included: true },
  { text: '基础搜索筛选', included: true },
  { text: '发送文字消息（每日5条）', included: true },
  { text: '前3个月完全免费', included: true },
  { text: '第4个月起 ¥5/月', included: true },
  { text: '查看完整联系方式', included: false },
  { text: '无限视频通话', included: false },
  { text: '高级智能匹配', included: false },
  { text: '置顶展示资料', included: false },
  { text: '专属情感顾问', included: false },
];

const RESELLER_FEATURES = [
  { text: '永久免费会员资格', included: true },
  { text: '浏览会员基本资料', included: true },
  { text: '每日10次喜欢', included: true },
  { text: '查看谁喜欢了您', included: true },
  { text: '基础搜索筛选', included: true },
  { text: '发送文字消息（每日5条）', included: true },
  { text: '专属分销商后台', included: true },
  { text: '实时佣金追踪', included: true },
  { text: '查看完整联系方式', included: false },
  { text: '无限视频通话', included: false },
  { text: '高级智能匹配', included: false },
  { text: '置顶展示资料', included: false },
];

const GOLD_FEATURES = [
  { text: '浏览会员基本资料', included: true },
  { text: '无限次喜欢', included: true },
  { text: '查看谁喜欢了您', included: true },
  { text: '高级多维度搜索筛选', included: true },
  { text: '无限发送文字消息', included: true },
  { text: '查看完整联系方式', included: true },
  { text: '无限视频通话', included: true },
  { text: '高级智能匹配算法', included: true },
  { text: '置顶展示资料（曝光量+300%）', included: true },
  { text: '专属情感顾问（每月2次）', included: true },
  { text: '学历优先匹配算法', included: true },
  { text: '全球800城市会员搜索', included: true },
];

const COMPARISON = [
  { feature: '浏览会员资料', basic: '基础信息', reseller: '基础信息', gold: '完整资料+联系方式' },
  { feature: '每日喜欢次数', basic: '10次', reseller: '10次', gold: '无限次' },
  { feature: '发送消息', basic: '每日5条', reseller: '每日5条', gold: '无限条' },
  { feature: '视频通话', basic: '不支持', reseller: '不支持', gold: '无限次' },
  { feature: '搜索筛选', basic: '基础筛选', reseller: '基础筛选', gold: '高级多维度筛选' },
  { feature: '资料展示', basic: '普通排序', reseller: '普通排序', gold: '置顶+优先展示' },
  { feature: '匹配算法', basic: '基础匹配', reseller: '基础匹配', gold: '学历优先智能匹配' },
  { feature: '情感顾问', basic: '不支持', reseller: '不支持', gold: '每月2次专属咨询' },
  { feature: '全球城市搜索', basic: '不支持', reseller: '不支持', gold: '800大城市全覆盖' },
  { feature: '分销佣金', basic: '不支持', reseller: '专属佣金体系', gold: '不支持' },
  { feature: '会员费用', basic: '前3月免费，后¥5/月', reseller: '永久免费', gold: '¥10/月' },
];

const FAQ = [
  { q: '工厂会员前3个月免费是什么意思？', a: '注册工厂会员后，前3个月完全免费享受所有工厂会员权益，无需绑定任何支付方式。第4个月起自动转为¥5/月的付费计划，届时会提前通知您。' },
  { q: '分销商会员为什么是免费的？', a: '分销商会员通过推广平台获得佣金收入，平台以此方式回馈推广伙伴，因此分销商会员资格永久免费，无任何隐藏费用。' },
  { q: '如何取消自动续费？', a: '您可以随时在"账户设置 → 会员管理"中关闭自动续费，取消后当前周期内权益仍然有效。' },
  { q: '支持哪些支付方式？', a: '我们支持微信支付、银行卡（Visa/Mastercard/银联）、信用卡及PayPal，覆盖国内外主流支付方式。' },
  { q: '可以申请退款吗？', a: '购买后7天内如未使用核心权益，可申请全额退款。超过7天或已使用核心功能则不支持退款。' },
  { q: '黄金会员和普通会员有什么区别？', a: '黄金会员享有无限消息、视频通话、置顶展示、专属情感顾问及全球城市搜索等全部高级功能，是寻找真实缘分的最佳选择。' },
  { q: '海外用户如何支付？', a: '海外用户可使用PayPal或国际信用卡（Visa/Mastercard）支付，系统自动按当日汇率换算。' },
];

const MEMBERSHIP_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '富缘佳旺会员方案 - 普通会员与黄金会员',
    description: '富缘佳旺提供普通会员（¥5/月）和黄金会员（¥10/月）两种方案，享受视频通话、无限消息、智能匹配等专属权益。',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/membership`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
        { '@type': 'ListItem', position: 2, name: '会员方案', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/membership` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '富缘佳旺会员方案',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Product',
          name: '普通会员',
          description: '每日10次喜欢，查看谁喜欢了您，基础搜索筛选，发送文字消息（每日5条）',
          offers: {
            '@type': 'Offer',
            price: '5',
            priceCurrency: 'CNY',
            priceValidUntil: '2027-12-31',
            availability: 'https://schema.org/InStock',
          },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Product',
          name: '黄金会员',
          description: '无限次喜欢，无限发送文字消息，无限视频通话，高级智能匹配算法，全球800城市会员搜索',
          offers: {
            '@type': 'Offer',
            price: '10',
            priceCurrency: 'CNY',
            priceValidUntil: '2027-12-31',
            availability: 'https://schema.org/InStock',
          },
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '如何取消自动续费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以随时在"账户设置 → 会员管理"中关闭自动续费，取消后当前周期内权益仍然有效。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些支付方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们支持微信支付、银行卡（Visa/Mastercard/银联）、信用卡及PayPal，覆盖国内外主流支付方式。',
        },
      },
      {
        '@type': 'Question',
        name: '可以申请退款吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '购买后7天内如未使用核心权益，可申请全额退款。超过7天或已使用核心功能则不支持退款。',
        },
      },
      {
        '@type': 'Question',
        name: '黄金会员和普通会员有什么区别？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄金会员享有无限消息、视频通话、置顶展示、专属情感顾问及全球城市搜索等全部高级功能，是寻找真实缘分的最佳选择。',
        },
      },
      {
        '@type': 'Question',
        name: '海外用户如何支付？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '海外用户可使用PayPal或国际信用卡（Visa/Mastercard）支付，系统自动按当日汇率换算。',
        },
      },
    ],
  },
];

export default function MembershipPage() {
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'gold' | 'reseller' | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: plansRef, isVisible: plansVisible } = useScrollAnimation();
  const { ref: compRef, isVisible: compVisible } = useScrollAnimation();
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <SEOHead
        title="富缘佳旺会员方案 - 工厂会员前3月免费 | 分销商永久免费 | FortuneMatch"
        description="富缘佳旺提供工厂会员（前3月免费，后¥5/月）、分销商会员（永久免费）和黄金会员（¥10/月）三种方案。享受视频通话、无限消息、智能匹配等专属权益。"
        keywords="富缘佳旺会员,婚恋平台会员,黄金会员,分销商会员,工厂会员免费"
        canonical="/membership"
        jsonLd={MEMBERSHIP_JSONLD}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/987c9342f308e6d91977c2953d673a7b.jpg"
            alt="Membership"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>
        <div
          ref={headerRef}
          className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-20 text-center transition-all duration-700"
          style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-5 py-2 mb-6">
            <i className="ri-vip-crown-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-bold">富缘佳旺会员中心</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            开通会员<br />
            <span className="text-yellow-300">解锁您的幸运缘分</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-4">
            我们是纯线上婚恋交友平台，价格超实惠，只服务真实、善良、有缘分的人。
            工厂会员前3个月完全免费，分销商会员永久免费！
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {['工厂会员前3月免费', '分销商永久免费', '真实认证会员', '全球800城市覆盖', '只服务善良有缘之人'].map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm text-white/80">
                <i className="ri-heart-line text-rose-300"></i>{tag}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 justify-center text-sm text-white/70">
            {['随时取消', '7天退款保障', '安全加密支付', '无隐藏费用'].map((t, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <i className="ri-check-line text-green-400"></i>{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        <div
          ref={plansRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700"
          style={{ opacity: plansVisible ? 1 : 0, transform: plansVisible ? 'translateY(0)' : 'translateY(40px)' }}
        >
          {/* Factory/Basic Plan */}
          <PlanCard
            name="工厂会员"
            nameEn="FACTORY MEMBERSHIP"
            price={0}
            priceNote="前3个月免费"
            subNote="第4个月起 ¥5/月"
            period="月"
            features={BASIC_FEATURES}
            highlight={false}
            icon="ri-building-line"
            iconBg="bg-white/20"
            gradient="from-red-700 to-red-600"
            borderColor="border-red-200"
            onSelect={() => setSelectedPlan('basic')}
            ctaLabel="免费开始"
            ctaIcon="ri-gift-line"
          />

          {/* Reseller Plan */}
          <PlanCard
            name="分销商会员"
            nameEn="RESELLER MEMBERSHIP"
            price={0}
            priceNote="永久免费"
            subNote="推广赚佣金"
            period="月"
            badge="永久免费"
            badgeColor="bg-emerald-500 text-white"
            features={RESELLER_FEATURES}
            highlight={false}
            icon="ri-share-line"
            iconBg="bg-white/20"
            gradient="from-emerald-600 to-teal-500"
            borderColor="border-emerald-200"
            onSelect={() => setSelectedPlan('reseller')}
            ctaLabel="立即申请分销"
            ctaIcon="ri-share-line"
          />

          {/* Gold Plan */}
          <PlanCard
            name="黄金会员"
            nameEn="GOLD MEMBERSHIP"
            price={10}
            period="月"
            badge="最受欢迎"
            badgeColor="bg-white text-amber-600"
            features={GOLD_FEATURES}
            highlight={true}
            icon="ri-vip-crown-line"
            iconBg="bg-white/20"
            gradient="from-amber-600 to-yellow-500"
            borderColor="border-amber-300"
            onSelect={() => setSelectedPlan('gold')}
            ctaLabel="立即升级黄金会员"
            ctaIcon="ri-vip-crown-line"
          />
        </div>

        {/* Payment Methods Banner */}
        <div
          className="mt-10 bg-gray-50 rounded-2xl p-6 text-center transition-all duration-700"
          style={{ opacity: plansVisible ? 1 : 0, transitionDelay: '200ms' }}
        >
          <p className="text-xs text-gray-500 font-semibold mb-4 tracking-widest uppercase">支持的支付方式</p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            {[
              { icon: 'ri-wechat-pay-line', label: '微信支付', color: 'text-green-600', bg: 'bg-green-50' },
              { icon: 'ri-bank-card-line', label: '银行卡', color: 'text-gray-700', bg: 'bg-gray-100' },
              { icon: 'ri-visa-line', label: 'Visa', color: 'text-gray-700', bg: 'bg-gray-100' },
              { icon: 'ri-mastercard-line', label: 'Mastercard', color: 'text-gray-700', bg: 'bg-gray-100' },
              { icon: 'ri-paypal-line', label: 'PayPal', color: 'text-sky-600', bg: 'bg-sky-50' },
            ].map((p, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <div className={`w-12 h-12 flex items-center justify-center ${p.bg} rounded-xl`}>
                  <i className={`${p.icon} ${p.color} text-2xl`}></i>
                </div>
                <span className="text-xs text-gray-500">{p.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">
            <i className="ri-lock-line text-green-500 mr-1"></i>
            所有支付均经过256位SSL加密，安全可靠。海外用户支持PayPal及国际信用卡。
          </p>
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-gray-50 py-16">
        <div
          ref={compRef}
          className="max-w-5xl mx-auto px-4 md:px-8 transition-all duration-700"
          style={{ opacity: compVisible ? 1 : 0, transform: compVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              功能对比
            </h2>
            <p className="text-gray-400 text-sm">详细了解三种会员方案的功能差异</p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-100">
              <div className="p-4 text-sm font-semibold text-gray-500">功能</div>
              <div className="p-4 text-center">
                <div className="text-sm font-bold text-red-600">工厂会员</div>
                <div className="text-sm font-bold text-gray-800">前3月免费</div>
                <div className="text-xs text-gray-400">后¥5/月</div>
              </div>
              <div className="p-4 text-center bg-emerald-50">
                <div className="text-sm font-bold text-emerald-600">分销商会员</div>
                <div className="text-sm font-bold text-gray-800">永久免费</div>
                <div className="text-xs text-gray-400">推广赚佣金</div>
              </div>
              <div className="p-4 text-center bg-amber-50">
                <div className="text-sm font-bold text-amber-600">黄金会员</div>
                <div className="text-lg font-bold text-gray-800">¥10<span className="text-xs text-gray-400">/月</span></div>
              </div>
            </div>

            {COMPARISON.map((row, i) => (
              <div key={i} className={`grid grid-cols-4 border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
                <div className="p-4 text-center text-sm text-gray-500">{row.basic}</div>
                <div className="p-4 text-center text-sm font-medium text-emerald-700 bg-emerald-50/30">{row.reseller}</div>
                <div className="p-4 text-center text-sm font-semibold text-amber-700 bg-amber-50/50">{row.gold}</div>
              </div>
            ))}

            {/* CTA Row */}
            <div className="grid grid-cols-4 p-4 gap-3">
              <div></div>
              <div>
                <button
                  onClick={() => setSelectedPlan('basic')}
                  className="w-full py-2.5 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  免费开始
                </button>
              </div>
              <div>
                <button
                  onClick={() => setSelectedPlan('reseller')}
                  className="w-full py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  申请分销
                </button>
              </div>
              <div>
                <button
                  onClick={() => setSelectedPlan('gold')}
                  className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-sm font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  升级黄金会员
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16 max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>会员真实评价</h2>
          <p className="text-gray-400 text-sm">他们通过黄金会员找到了真爱</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: '陈先生', city: '🇨🇳 上海', plan: '黄金会员', text: '升级黄金会员后，视频通话功能让我在见面前就确认了对方的真实性，3个月后我们就在一起了！', avatar: 'https://readdy.ai/api/search-image?query=happy%20Chinese%20man%20portrait%2C%20warm%20smile%2C%20professional%20appearance%2C%20clean%20background%2C%20age%2032&width=100&height=100&seq=test001&orientation=squarish' },
            { name: '李女士', city: '🇸🇬 新加坡', plan: '黄金会员', text: '身在海外，通过全球城市搜索功能找到了同在新加坡的他，现在我们已经订婚了！感谢富缘佳旺！', avatar: 'https://readdy.ai/api/search-image?query=happy%20Chinese%20woman%20portrait%2C%20warm%20smile%2C%20elegant%20appearance%2C%20clean%20background%2C%20age%2028&width=100&height=100&seq=test002&orientation=squarish' },
            { name: '王先生', city: '🇬🇧 伦敦', plan: '黄金会员', text: '学历优先匹配算法真的很准，匹配到的都是同等教育背景的优质女性，现在已经在认真交往了。', avatar: 'https://readdy.ai/api/search-image?query=happy%20Chinese%20man%20portrait%2C%20confident%20smile%2C%20casual%20style%2C%20clean%20background%2C%20age%2030&width=100&height=100&seq=test003&orientation=squarish' },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.city}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <i className="ri-vip-crown-fill text-amber-500 text-xs"></i>
                    <span className="text-xs text-amber-600 font-medium">{t.plan}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-gray-50 py-16">
        <div
          ref={faqRef}
          className="max-w-3xl mx-auto px-4 md:px-8 transition-all duration-700"
          style={{ opacity: faqVisible ? 1 : 0, transform: faqVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>常见问题</h2>
            <p className="text-gray-400 text-sm">关于会员和支付的常见疑问</p>
          </div>
          <div className="flex flex-col gap-3">
            {FAQ.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-800">{item.q}</span>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-300 flex-shrink-0 ml-3 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="py-16 text-center px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
          还在等什么？
        </h2>
        <p className="text-gray-500 text-sm mb-6">工厂会员前3个月免费 · 分销商会员永久免费</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
          <button
            onClick={() => setSelectedPlan('basic')}
            className="px-8 py-3 border-2 border-red-600 text-red-600 rounded-full font-semibold text-sm hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-gift-line mr-1"></i> 工厂会员 · 前3月免费
          </button>
          <button
            onClick={() => setSelectedPlan('reseller')}
            className="px-8 py-3 border-2 border-emerald-600 text-emerald-600 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-line mr-1"></i> 分销商会员 · 永久免费
          </button>
          <button
            onClick={() => setSelectedPlan('gold')}
            className="px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full font-semibold text-sm hover:from-amber-600 hover:to-yellow-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line mr-1"></i> ¥10/月 · 升级黄金会员
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          <i className="ri-shield-check-line text-green-500 mr-1"></i>
          7天退款保障 · 随时取消 · 无隐藏费用
        </p>
      </div>

      <Footer />

      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  );
}
